import Vue from 'vue'
import Vuetify from "./plugins/vuetify.js";
import App from './App.vue'

import router from "./plugins/router.js";
import acl from './plugins/acl.js'

import axios from "axios";
import store from "./stores/store";



Vue.config.productionTip = false


axios.defaults.baseURL = process.env.VUE_APP_API_ENDPOINT;
axios.defaults.withCredentials = true;

// Endpointy, dla ktorych 401 jest normalna odpowiedzia i ktore nigdy nie moga
// uruchamiac odswiezania tokenu - inaczej powstalaby petla.
const AUTH_ENDPOINTS = ["login_check", "token/refresh", "logout"];

// Ile razy jedno zadanie moze zostac powtorzone po odswiezeniu tokenu.
// Wczesniej byla tylko jedna proba, wiec drugie 401 natychmiast wylogowywalo.
const MAX_REQUEST_RETRIES = 2;

// JWT ma ttl 30 minut, wiec kazda karta odswieza token kilkanascie razy dziennie.
// Pojedynczy chwilowy blad sieci nie moze konczyc sesji - stad ponawianie.
const REFRESH_ATTEMPTS = 3;
const REFRESH_BACKOFF_MS = [500, 2000];

let refreshPromise = null;

function wait(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function isAuthEndpoint(url) {
  return AUTH_ENDPOINTS.some(endpoint => url.includes(endpoint));
}

// Blad przejsciowy: brak odpowiedzi (timeout, zerwane polaczenie, zablokowany
// preflight) albo blad serwera/proxy. Takie przypadki nalezy ponowic.
// Dopiero jednoznaczna odpowiedz 4xx z token/refresh oznacza, ze refresh token
// wygasl albo zostal uniewazniony - i tylko wtedy wylogowujemy uzytkownika.
function isTransientError(err) {
  if (!err.response) {
    return true;
  }

  const status = err.response.status;

  return status === 408 || status === 429 || status >= 500;
}

function isOnLoginPage() {
  // Trasa "login" ma bezimienne dziecko o pustej sciezce, wiec po wejsciu na
  // /login currentRoute.name bywa undefined - dlatego sprawdzamy tez sciezke.
  return router.currentRoute.name === "login" || router.currentRoute.path === "/login";
}

function goToLogin() {
  if (!isOnLoginPage()) {
    router.push({ name: "login" }).catch(() => {});
  }
}

function clearSessionState() {
  store.commit("SET_LOGGED", false);
  store.commit("SET_CURRENT_USER", {});
  localStorage.removeItem('user/currentUser');
}

function endSession() {
  clearSessionState();
  goToLogin();
}

async function runRefresh() {
  let lastError;

  for (let attempt = 0; attempt < REFRESH_ATTEMPTS; attempt++) {
    try {
      await store.dispatch("REFRESH_TOKEN");
      return;
    } catch (err) {
      lastError = err;

      if (!isTransientError(err)) {
        err.__sessionExpired = true;
        throw err;
      }

      if (attempt < REFRESH_ATTEMPTS - 1) {
        await wait(REFRESH_BACKOFF_MS[attempt]);
      }
    }
  }

  // Wylacznie bledy przejsciowe - sesja moze byc nadal wazna, wiec odrzucamy
  // zawieszone zadania, ale NIE wylogowujemy uzytkownika.
  console.error("Nie udalo sie odswiezyc tokenu, sesja pozostaje aktywna", lastError);

  throw lastError;
}

// Jedno odswiezenie na karte niezaleznie od liczby rownoleglych 401.
// Trzymanie obietnicy zamiast flagi i kolejki subskrybentow usuwa okno, w ktorym
// zadanie mogloby trafic do kolejki juz po jej oproznieniu i nigdy nie zostac
// rozwiazane (zawieszony request).
function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = runRefresh()
      .catch(err => {
        if (err && err.__sessionExpired) {
          endSession();
        }

        throw err;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

axios.interceptors.response.use(
  response => response,
  err => {
    const originalRequest = err.config;
    const status = err.response && err.response.status;
    const url = (originalRequest && originalRequest.url) || "";

    if (!originalRequest || status !== 401 || isAuthEndpoint(url)) {
      return Promise.reject(err);
    }

    originalRequest.__retryCount = originalRequest.__retryCount || 0;

    if (originalRequest.__retryCount >= MAX_REQUEST_RETRIES) {
      // Token zostal odswiezony, a zadanie nadal zwraca 401. To nie jest wygasla
      // sesja, wiec blad wraca do wywolujacego zamiast wylogowywac z aplikacji.
      return Promise.reject(err);
    }

    originalRequest.__retryCount += 1;

    return refreshSession().then(() => axios(originalRequest));
  }
);

// localStorage jest wspolny dla wszystkich kart tego origin, wiec zakonczenie
// sesji w jednej karcie musi byc odzwierciedlone w pozostalych. Zdarzenie
// storage dociera tylko do innych kart niz ta, ktora zmienila wartosc.
window.addEventListener("storage", event => {
  if (event.key === "user/currentUser" && event.newValue === null) {
    store.commit("SET_LOGGED", false);
    store.commit("SET_CURRENT_USER", {});
    goToLogin();
  }
});

new Vue({
  acl,
  store,
  router,
  vuetify: Vuetify,
  render: h => h(App),
}).$mount('#app')
